import { computed, Ref } from 'vue';
import { BundleDetails, ProductComplete, ProductSelection } from '@/features/Product/types/models';

export type BundlePriceResult = {
  currentPrice: number;
  fromPrice: number;
  displayPrice: number;
  isFixedPrice: boolean;
};

// Currently does nothing but is reserved for later logic updates
const calculateFixedPrice = (price: number): number => {
  return price;
};

const applyDiscounts = (
  total: number,
  discountPercentage?: number,
  discountAmount?: number,
): number => {
  let discountedTotal = total;

  if (discountPercentage) {
    discountedTotal = discountedTotal * (1 - discountPercentage / 100);
  }
  if (discountAmount) {
    discountedTotal = discountedTotal - discountAmount;
  }

  const minPrice = 0;
  return Math.max(minPrice, discountedTotal);
};

const calculateSumPrice = (
  selectedProducts: ProductSelection[],
  discountPercentage?: number,
  discountAmount?: number,
): number => {
  const total = selectedProducts.reduce((sum, item) => {
    return sum + item.product.attributes.price * item.quantity;
  }, 0);

  return applyDiscounts(total, discountPercentage, discountAmount);
};

const calculateFirstXPaidPrice = (selectedProducts: ProductSelection[]): number => {
  // Checkout app expects dollars
  const centsToDollars = 100;
  return selectedProducts.reduce((total, item) => {
    return total + (item.price * item.quantity * centsToDollars || 0);
  }, 0);
};

const getCheapestBundleBuild = (bundleDefinition: BundleDetails): ProductSelection[] => {
  const selections: ProductSelection[] = [];
  const { bundleItems } = bundleDefinition;

  bundleItems.forEach((item) => {
    const requiredCount = item.isRequired ? item.minSelection || 1 : 0;

    if (requiredCount < 1 || !item.processedProducts) return;

    const availableProducts = item.processedProducts
      .filter((product) => !product.attributes.soldOut)
      .sort((a, b) => a.attributes.price - b.attributes.price);

    let remainingToSelect = requiredCount;

    for (const product of availableProducts) {
      if (remainingToSelect <= 0) break;

      const maxQuantity = product.attributes.maxCartQuantity || 5;
      const quantityToAdd = Math.min(remainingToSelect, maxQuantity);

      selections.push({
        product,
        quantity: quantityToAdd,
      });

      remainingToSelect -= quantityToAdd;
    }
  });

  return selections;
};

export const useBundlePricing = (
  bundleDefinition: BundleDetails | null | undefined,
  selectedProducts?: Ref<ProductSelection[]>,
) => {
  const calculateMinimumPrice = (bundle: BundleDetails): number => {
    const { pricingRules, minItems } = bundle;

    switch (pricingRules.type) {
      case 'fixed':
        return calculateFixedPrice(pricingRules.price);

      case 'sum': {
        const cheapestSelections = getCheapestBundleBuild(bundle);
        const total = cheapestSelections.reduce((sum, selection) => {
          return sum + selection.product.attributes.price * selection.quantity;
        }, 0);

        return applyDiscounts(total, pricingRules.discountPercentage, pricingRules.discountAmount);
      }

      case 'first_x_paid': {
        const cheapestSelections = getCheapestBundleBuild(bundle);
        const paidCount = pricingRules.paidItemsCount || minItems || 0;

        let itemCount = 0;
        let total = 0;

        for (const selection of cheapestSelections) {
          const quantityToPay = Math.min(selection.quantity, paidCount - itemCount);
          total += selection.product.attributes.price * quantityToPay;
          itemCount += quantityToPay;

          if (itemCount >= paidCount) break;
        }

        return total;
      }

      case 'first_x_free': {
        const cheapestSelections = getCheapestBundleBuild(bundle);
        const freeCount = pricingRules.freeItemsCount || 1;

        let itemCount = 0;
        let total = 0;

        for (const selection of cheapestSelections) {
          const quantityFree = Math.min(selection.quantity, freeCount - itemCount);
          const quantityToPay = selection.quantity - quantityFree;
          total += selection.product.attributes.price * quantityToPay;
          itemCount += quantityFree;
        }

        return total;
      }

      default:
        return 0;
    }
  };

  const calculateCurrentPrice = (
    bundle: BundleDetails,
    selectedProducts: { product: ProductComplete; quantity: number; price?: number }[],
  ): number => {
    const { pricingRules } = bundle;

    switch (pricingRules.type) {
      case 'fixed':
        return calculateFixedPrice(pricingRules.price);

      case 'sum':
        return calculateSumPrice(
          selectedProducts,
          pricingRules.discountPercentage,
          pricingRules.discountAmount,
        );

      case 'first_x_paid':
      case 'first_x_free':
        return calculateFirstXPaidPrice(selectedProducts);

      default:
        return 0;
    }
  };

  const fromPrice = computed(() => {
    if (!bundleDefinition) return 0;
    return calculateMinimumPrice(bundleDefinition);
  });

  const currentPrice = computed(() => {
    if (!bundleDefinition) return 0;

    if (selectedProducts.value.length === 0) {
      return fromPrice.value;
    }

    return calculateCurrentPrice(bundleDefinition, selectedProducts.value);
  });

  const isFixedPrice = computed(() => {
    return bundleDefinition?.pricingRules.type === 'fixed';
  });

  const displayPrice = computed(() => currentPrice.value);

  const bundlePriceInfo = computed<BundlePriceResult>(() => ({
    currentPrice: currentPrice.value,
    fromPrice: fromPrice.value,
    displayPrice: displayPrice.value,
    isFixedPrice: isFixedPrice.value,
  }));

  return {
    currentPrice,
    fromPrice,
    displayPrice,
    isFixedPrice,
    bundlePriceInfo,
    calculateMinimumPrice,
    calculateCurrentPrice,
  };
};
